import React, { useEffect, useState } from 'react';
import {
  Grid,
  Box,
  Button,
  MenuItem
} from '@material-ui/core';
import { TextField, Select } from '@material-ui/core';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import { toast } from "react-toastify";
import { makeStyles } from '@material-ui/core/styles';
import sousfamilleService from '../services/SousfamilleService';
import FamileService from '../services/FamileService';

// Icônes Material-UI
import AddIcon from '@material-ui/icons/Add';
import CategoryIcon from '@material-ui/icons/Category';
import LocalOfferIcon from '@material-ui/icons/LocalOffer';

const useStyles = makeStyles((theme) => ({
  formContainer: {
    marginTop: theme.spacing(1),
  },
  gridItem: {
    padding: theme.spacing(1),
  },
  iconAdornment: {
    color: '#226D68',
    marginRight: theme.spacing(1),
  },
  submitButton: {
    background: 'linear-gradient(135deg, #226D68 0%, #2a8c82 100%)',
    color: 'white',
    fontWeight: 600,
    fontSize: '1rem',
    padding: '12px 40px',
    borderRadius: 8,
    boxShadow: '0 4px 15px rgba(34, 109, 104, 0.3)',
    textTransform: 'none',
    width: '100%',
    marginTop: theme.spacing(2),
    '&:hover': {
      background: 'linear-gradient(135deg, #1a5a55 0%, #226D68 100%)',
      boxShadow: '0 6px 20px rgba(34, 109, 104, 0.4)',
    },
    '&:disabled': {
      background: '#cbd5e1',
      boxShadow: 'none',
    },
  },
  formHeader: {
    textAlign: 'center',
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
  },
  formTitle: {
    fontWeight: 700,
    color: '#226D68',
    marginBottom: theme.spacing(1),
    fontSize: '1.5rem',
  },
  formSubtitle: {
    color: '#64748b',
    fontSize: '0.9rem',
  },
  sectionTitle: {
    fontWeight: 600,
    color: '#226D68',
    marginBottom: theme.spacing(2),
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  selectField: {
    '& .MuiSelect-root': {
      display: 'flex',
      alignItems: 'center',
    },
  },
}));

const SousFamilleForm = ({ onClose, refresh }) => {
  const classes = useStyles();
  const auth = useSelector((state) => state.auth);
  const [familles, setFamilles] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await FamileService.getFamilles();
        setFamilles(response.data || []);
      } catch (error) {
        console.error('Erreur chargement familles:', error);
        toast.error("Erreur lors du chargement des familles");
      }
    };

    fetchData();
  }, []);

  const initialValues = {
    intituleFam: '', 
    designation: '', 
  };

  const validationSchema = Yup.object().shape({
    intituleFam: Yup.string().required("Requis"),
    designation: Yup.string().min(2, "Trop court").required("Requis"),
  });

  const onSubmit = async (values, props) => {
    try {
      const sousFamille = {
        "intituleFam": values.intituleFam.trim(),
        "designation": values.designation.trim(),
        "speudo": auth.nom,
      };

      await sousfamilleService.createsousFamille(sousFamille);
      
      toast.success("Sous-famille ajoutée avec succès", {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      
      props.resetForm();
      
      // Fermer le dialog et rafraîchir si nécessaire
      if (onClose) onClose();
      if (refresh) refresh();
      
    } catch (error) {
      console.error('Erreur ajout sous-famille:', error);
      toast.error("❌ Erreur lors de l'ajout de la sous-famille", {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  };

  return (
    <Box>
      {/* En-tête du formulaire */}
      <Box className={classes.formHeader}>
        <LocalOfferIcon style={{ fontSize: 40, color: '#226D68', marginBottom: 8 }} />
        <div className={classes.formTitle}>Nouvelle Sous-Famille</div>
        <div className={classes.formSubtitle}>Ajoutez une nouvelle sous-famille pharmaceutique</div>
      </Box>

      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
        {(props) => (
          <Form noValidate className={classes.formContainer}>
            <Grid container spacing={2}>
              
              {/* Informations de la sous-famille */}
              <Grid item xs={12}>
                <div className={classes.sectionTitle}>
                  <CategoryIcon /> Informations de la sous-famille
                </div>
              </Grid>

              {/* Sélection de la famille */}
              <Grid item xs={12} className={classes.gridItem}>
                <Field 
                  as={Select}
                  name="intituleFam"
                  label="Famille parente"
                  fullWidth
                  variant="outlined"
                  size="small"
                  error={props.errors.intituleFam && props.touched.intituleFam}
                  helperText={<ErrorMessage name='intituleFam' />}
                  required
                  className={classes.selectField}
                  displayEmpty
                  inputProps={{
                    startAdornment: <CategoryIcon className={classes.iconAdornment} />
                  }}
                >
                  <MenuItem value="" disabled>
                    <CategoryIcon style={{ marginRight: 8, color: '#226D68' }} />
                    Sélectionnez une famille
                  </MenuItem>
                  {familles.map((famille) => (
                    <MenuItem key={famille.intituleFam} value={famille.intituleFam}>
                      {famille.intituleFam}
                    </MenuItem>
                  ))}
                </Field>
              </Grid>

              {/* Désignation de la sous-famille */}
              <Grid item xs={12} className={classes.gridItem}>
                <Field 
                  as={TextField} 
                  name='designation' 
                  label='Désignation de la sous-famille' 
                  fullWidth 
                  variant="outlined"
                  size="small"
                  error={props.errors.designation && props.touched.designation}
                  helperText={<ErrorMessage name='designation' />} 
                  required 
                  InputProps={{
                    startAdornment: <LocalOfferIcon className={classes.iconAdornment} />
                  }}
                  placeholder="Ex: Antibiotiques à large spectre, Antalgiques forts, etc."
                />
              </Grid>

              {/* Bouton de soumission */}
              <Grid item xs={12} className={classes.gridItem}>
                <Button 
                  type='submit' 
                  variant='contained'
                  className={classes.submitButton}
                  startIcon={<AddIcon />}
                  disabled={props.isSubmitting}
                >
                  {props.isSubmitting ? 'Ajout en cours...' : 'Ajouter la sous-famille'}
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Box>
  );
}

export default SousFamilleForm;